import { lazy } from "react";
import {
  createRouter,
  createRoute,
  createRootRoute,
  redirect,
  Outlet,
} from "@tanstack/react-router";
import { AppLayout } from "@/components/AppLayout";
import { LandingPage } from "@/pages/LandingPage";
import { PrivacidadePage } from "@/pages/PrivacidadePage";
import { TermosPage } from "@/pages/TermosPage";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { AccountPage } from "@/pages/AccountPage";
import { GruposPage } from "@/pages/GruposPage";
import { HistoricoPage } from "@/pages/HistoricoPage";
import { LinksPage } from "@/pages/LinksPage";
import { LinksEmMassaPage } from "@/pages/LinksEmMassaPage";
import { VitrinePage } from "@/pages/VitrinePage";
import { WhatsAppPage } from "@/pages/WhatsAppPage";
import { BioPage } from "@/pages/BioPage";
import { UtmPage } from "@/pages/UtmPage";
import { ComissoesPage } from "@/pages/ComissoesPage";
import { AnalyticsPage } from "@/pages/AnalyticsPage";
import { MembrosPage } from "@/pages/MembrosPage";
import { RodizioPage } from "@/pages/RodizioPage";
import { FeedPage } from "@/pages/FeedPage";
import { AutoPromocoesPage } from "@/pages/AutoPromocoesPage";
import { SettingsIntegracaoPage } from "@/pages/SettingsIntegracaoPage";
import { SettingsEmpresaPage } from "@/pages/SettingsEmpresaPage";
import { SettingsPersonalizacaoPage } from "@/pages/SettingsPersonalizacaoPage";
import { EncurtadorPage } from "@/pages/EncurtadorPage";
import { CalendarioPage } from "@/pages/CalendarioPage";
import { DownloadsPage } from "@/pages/DownloadsPage";
import { CriarPostsPage } from "@/pages/CriarPostsPage";
import { ExtensaoPage } from "@/pages/ExtensaoPage";
import { RecorrentesPage } from "@/pages/RecorrentesPage";
import { TextosDisparoPage } from "@/pages/TextosDisparoPage";
import { StoriesPage } from "@/pages/StoriesPage";
import { getSessionSafe } from "@/lib/supabase";

// Pagina de planos: lazy para nao pesar o bundle inicial do painel.
const PlansPage = lazy(() =>
  import("@/pages/PlansPage").then((mod) => ({ default: mod.PlansPage })),
);

// Guards de rota
//
// Use getSessionSafe (timeout ~4s) instead of calling supabase.auth.getSession()
// directly: if the auth backend is unreachable, supabase-js can get stuck in
// internal retry and never resolve the promise, hanging beforeLoad (and the
// UI) indefinitely. With the timeout, an unavailable backend is treated as
// "no confirmed session" and falls back safely to /login (never authenticates
// by mistake, never hangs the screen).
export async function requireAuth(): Promise<void> {
  const session = await getSessionSafe();
  if (!session) {
    throw redirect({ to: "/login" });
  }
}

export async function requireGuest(): Promise<void> {
  const session = await getSessionSafe();
  if (session) {
    throw redirect({ to: "/app" });
  }
}

// Rota raiz
const rootRoute = createRootRoute({
  component: Outlet,
});

// Landing publica (/)
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

// Politica de privacidade publica (/privacidade) - sem login
const privacidadeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacidade",
  component: PrivacidadePage,
});

// Termos de uso publicos (/termos) - sem login
const termosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/termos",
  component: TermosPage,
});

// Login (/login) - redireciona se ja autenticado
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: requireGuest,
  component: LoginPage,
});

// Cadastro (/cadastro) - redireciona se ja autenticado
const cadastroRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cadastro",
  beforeLoad: requireGuest,
  component: () => <LoginPage initialMode="signup" />,
});

// Layout de app protegido (/app/*)
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  beforeLoad: requireAuth,
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
});

// Dashboard (/app)
const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/",
  component: DashboardPage,
});

// Minha conta (/app/account)
const accountRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/account",
  component: AccountPage,
});

// Grupos (/app/grupos)
const gruposRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/grupos",
  component: GruposPage,
});

// Historico unificado (/app/conversoes, URL mantida para links antigos)
const conversoesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/conversoes",
  component: HistoricoPage,
});

// Links (/app/links)
const linksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/links",
  component: LinksPage,
});

// Links em massa (/app/links/massa)
const linksMassaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/links/massa",
  component: LinksEmMassaPage,
});

// Planos (/app/plans)
const plansRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/plans",
  component: PlansPage,
});

// Vitrine (/app/vitrine)
const vitrineRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/vitrine",
  component: VitrinePage,
});

// WhatsApp (/app/whatsapp)
const whatsappRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/whatsapp",
  component: WhatsAppPage,
});

// Link na Bio (/app/bio)
const bioRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/bio",
  component: BioPage,
});

// Gerador UTM (/app/utm)
const utmRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/utm",
  component: UtmPage,
});

// Comissoes (/app/comissoes)
const comissoesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/comissoes",
  component: ComissoesPage,
});

// Analytics (/app/analytics)
const analyticsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics",
  component: AnalyticsPage,
});

// Analytics > Membros e inscritos (/app/analytics/membros)
const analyticsMembrosRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics/membros",
  component: MembrosPage,
});

// Rodizio de grupos do WhatsApp (/app/rodizio)
const rodizioRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/rodizio",
  component: RodizioPage,
});

// Feed de Promocoes (/app/feed)
const feedRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/feed",
  component: FeedPage,
});

// Promocoes automaticas (/app/promocoes-automaticas)
const autoPromocoesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/promocoes-automaticas",
  component: AutoPromocoesPage,
});

// Encurtador (/app/encurtador)
const encurtadorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/encurtador",
  component: EncurtadorPage,
});

// Criar Posts (/app/criar-posts)
const criarPostsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/criar-posts",
  component: CriarPostsPage,
});

// Calendario (/app/calendario)
const calendarioRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/calendario",
  component: CalendarioPage,
});

// Publicacoes recorrentes (/app/recorrentes)
const recorrentesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/recorrentes",
  component: RecorrentesPage,
});

// Textos de disparo (/app/textos-de-disparo)
const textosDisparoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/textos-de-disparo",
  component: TextosDisparoPage,
});

// Templates de stories (/app/stories)
const storiesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/stories",
  component: StoriesPage,
});

// Downloads (/app/downloads)
const downloadsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/downloads",
  component: DownloadsPage,
});

// Extensão Chrome (/app/extensao)
const extensaoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/extensao",
  component: ExtensaoPage,
});

// Integracoes (/app/settings/integracao)
const settingsIntegracaoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings/integracao",
  component: SettingsIntegracaoPage,
});

// Empresa (/app/settings/empresa)
const settingsEmpresaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings/empresa",
  component: SettingsEmpresaPage,
});

// Personalizacao (/app/settings/personalizacao)
const settingsPersonalizacaoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings/personalizacao",
  component: SettingsPersonalizacaoPage,
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  privacidadeRoute,
  termosRoute,
  loginRoute,
  cadastroRoute,
  appLayoutRoute.addChildren([
    dashboardRoute,
    accountRoute,
    gruposRoute,
    conversoesRoute,
    linksRoute,
    linksMassaRoute,
    plansRoute,
    vitrineRoute,
    whatsappRoute,
    bioRoute,
    utmRoute,
    comissoesRoute,
    analyticsRoute,
    analyticsMembrosRoute,
    rodizioRoute,
    feedRoute,
    autoPromocoesRoute,
    encurtadorRoute,
    criarPostsRoute,
    calendarioRoute,
    recorrentesRoute,
    textosDisparoRoute,
    storiesRoute,
    downloadsRoute,
    extensaoRoute,
    settingsIntegracaoRoute,
    settingsEmpresaRoute,
    settingsPersonalizacaoRoute,
  ]),
]);

function RoutePending(): JSX.Element {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-primary" />
    </div>
  );
}

export const router = createRouter({
  routeTree,
  defaultPendingComponent: RoutePending,
});

// Tipagem global do TanStack Router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
